import React from 'react';

import {
    CheckCircleOutline,
    RadioButtonUnchecked,
    InfoOutlined,
    AdjustOutlined,
    ErrorOutline,
    HighlightOff,
} from '@mui/icons-material';

import { colors } from 'theme';

import { AntennaManager, ScheduleManager, SelfManager } from 'models';

/**
 * An awaitable wrapper around the `setTimeout` function.
 * @param ms The milliseconds to sleep for
 * @returns A Promise that resolves when the time has elapsed
 */
export function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export function debounce<T extends (...args: any[]) => any>(
    cb: T,
    wait: number,
) {
    let h: any;
    const callable = (...args: any) => {
        console.log(`Clearing Timeout ${h}`);
        clearTimeout(h);
        h = setTimeout(() => {
            console.log(`Calling Function`);
            cb(...args);
        }, wait);
    };
    return callable as T;
}

export function equalWithTolerance(a: number, b: number, tolerance: number) {
    return Math.abs(b - a) < tolerance;
}

/**
 * @param coords The coords to print in the form [longitude, latitude]
 * @returns A pretty string represeting the coordinates
 */
export const prettifyLngLat = (coords: number[]) => {
    const [longitude, latitude] = coords;
    const latDir = latitude > 0 ? 'N' : 'S';
    const lngDir = longitude < 0 ? 'W' : 'E';

    const values = [latitude, longitude].map(Math.abs).map((x) => x.toFixed(5));

    return `${values[0]}°${latDir}, ${values[1]}°${lngDir}`;
};

/**
 *
 * @param date The date to compare to from now
 */
export const toReadableTimeFromNow = (date?: Date | null): string => {
    if (!date) return 'n/a';
    /**
     * Positive values in the future, negative in the past
     */
    const signedAge = date.getTime() - Date.now();
    /**
     * abs age for easy comparisons
     */
    const age = Math.abs(signedAge);

    const isPast = signedAge < 0;

    const end = isPast ? ' ago' : '';
    const start = isPast ? '' : 'in ';

    if (age < 0) return "hasn't occured yet";
    if (age < 1000) return 'now';
    let time = Math.round(age / 1000);
    if (age < 60 * 1000)
        return `${start}${time} second${time === 1 ? '' : 's'}${end}`;
    time = Math.round(age / (1000 * 60));
    if (age < 60 * 60 * 1000)
        return `${start}${time} minute${time === 1 ? '' : 's'}${end}`;
    time = Math.round(age / (1000 * 60 * 60));
    if (age < 24 * 60 * 60 * 1000)
        return `${start}${time} hour${time === 1 ? '' : 's'}${end}`;
    time = Math.round(age / (1000 * 60 * 60 * 24));
    if (age < 7 * 24 * 60 * 60 * 1000)
        return `${start}${time} day${time === 1 ? '' : 's'}${end}`;
    time = Math.round(age / (1000 * 60 * 60 * 24 * 7));
    return `${start}${time} week${time === 1 ? '' : 's'}${end}`;
};

const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];

/**
 * @returns The date in a DD MMM format; Eg: '8 Jan'
 */
export const toDDMonFormat = (date: Date) => {
    return `${date.getDate()} ${months[date.getMonth()]}`;
};

/**
 * @returns The date in a DD/MM/YYYY format; Eg: '8/10/2022'
 */
export const toDDMMYYYYFormat = (date: Date) => {
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
};

/**
 * @returns The date in a HH:mm DD MMM YYYY format; Eg: '11:30 8 Jun 2022'
 */
export const toTimeDDMonYearFormat = (date: Date) => {
    return `${date.getHours() < 10 ? '0' : ''}${date.getHours()}:${
        date.getMinutes() < 10 ? '0' : ''
    }${date.getMinutes()} ${date.getDate()} ${
        months[date.getMonth()]
    } ${date.getFullYear()}`;
};

/**
 * @returns True if the provided date represents todays date. False otherwise
 */
export const isToday = (date: Date) =>
    new Date().toDateString() === date.toDateString();

/**
 * Changes the first letter of a string to an uppercase letter
 * @param str The string to capitalize
 * @returns The original string with the first letter capitalised
 */
export const capitalize = (str?: string | null): string | null => {
    return str ? str.charAt(0).toUpperCase() + str.slice(1) : null;
};

/**
 * Will round the given number to the given number of decimal places
 * @param value The number to round
 * @param decimals The number of decimal places to round to (default: 0)
 * @returns The rounded number
 */
export const round = (
    value: number | null | undefined,
    decimals = 0,
): number => {
    if (value == null) return NaN;
    // Makes this very quick for cases 0, 1, 2.
    // 3 and beyond are fast but not good for anything real time or for large datasets
    switch (decimals) {
        case 0:
            return Math.round(value);
        case 1:
            return Math.round(value * 10) / 10;
        case 2:
            return Math.round(value * 100) / 100;
        default:
    }
    const pow = 10 ** decimals;
    return Math.round(value * pow) / pow;
};

/**
 * Will normanise an angle into the range of [0,360)
 * @param angle Any number representing an angle or rotation
 * @returns The same number represented in the range from 0 to 360
 */
export const normaliseAngle = (angle: number) => ((angle % 360) + 360) % 360;

/**
 * Will convert an object into URLSearchParams
 * @param obj An object containing the data to convert
 * @returns A URLSearchParams instance containing data from the object
 */
export const ObjToURLSearchParams = (
    obj: Record<string, any>,
): URLSearchParams => {
    return new URLSearchParams(
        Object.keys(obj).reduce<Record<string, string>>((acc, key) => {
            acc[key] = `${obj[key] ?? ''}`;
            return acc;
        }, {}),
    );
};

/**
 * Check if something is an object.
 * @param item Item to check
 * @returns True if the item is an object. Enforces the type of the input
 */
export function isObject(item: any): item is Record<string, any> {
    return item && typeof item === 'object' && !Array.isArray(item);
}

/**
 * Will deeply merge two objects
 *
 * Primarily needed by tests when merging
 * Default redux state with any test specific state
 *
 * Algorithm 'heavily inspired' by
 * https://stackoverflow.com/a/37164538/12757511
 *
 */
export function mergeDeep<
    T extends Record<string, any>,
    S extends Record<string, any>,
>(target: T, source?: S): T {
    const output = Object.assign({}, target) as T;
    if (isObject(target) && isObject(source)) {
        Object.keys(source).forEach((key) => {
            if (isObject(source[key])) {
                if (!(key in target)) {
                    Object.assign(output, { [key]: source[key] });
                } else {
                    // @ts-ignore
                    output[key] = mergeDeep(target[key], source[key]);
                }
            } else {
                Object.assign(output, { [key]: source[key] });
            }
        });
    }
    return output;
}

/**
 * Check if a user has a particular roll
 * @param user The user to check
 * @param roles The roles to look for
 * @returns True if the user contains any one of the provided rolls. False otherwise
 */
export const hasGroup = (
    user: SelfManager.User | null | undefined,
    ...groups: SelfManager.Group[]
) => {
    if (!user || !user.groups) return false;

    if (user.isSuperuser) return true;

    return !!groups.find((group) => user.groups?.includes(group));
};

export const getColourFromScheduleStatus = (
    status: ScheduleManager.Schedule['status'],
) => {
    switch (status) {
        case 'booked':
        case 'auto-booked':
            return colors.green;
        case 'to_confirm':
            return 'blue';
        case 'archived':
            return colors.green;
        case 'skipped':
            return 'darkgoldenrod';
        case 'error':
            return colors.red;
        default:
            return 'black';
    }
};

export const getIconFromScheduleStatus = (
    status?: ScheduleManager.Schedule['status'],
) => {
    switch (status) {
        case 'booked':
        case 'auto_booked':
            return (
                <CheckCircleOutline
                    style={{ color: colors.green }}
                    fontSize="small"
                />
            );
        case 'to_confirm':
            return <InfoOutlined style={{ color: 'blue' }} fontSize="medium" />;
        case 'archived':
            return (
                <AdjustOutlined
                    style={{ color: colors.green }}
                    fontSize="medium"
                />
            );
        case 'skipped':
            return (
                <HighlightOff
                    style={{ color: 'darkgoldenrod' }}
                    fontSize="medium"
                />
            );
        case 'error':
            return (
                <ErrorOutline style={{ color: colors.red }} fontSize="medium" />
            );
        default:
            return (
                <RadioButtonUnchecked
                    style={{ color: 'black' }}
                    fontSize="medium"
                />
            );
    }
};

export const getColourFromStatus = (
    status: AntennaManager.Status,
): React.CSSProperties['color'] => {
    switch (status) {
        case 'idle':
            return colors.green;
        case 'inactive':
            return colors.red;
        case 'preparing':
            return 'lightblue';
        case 'executing':
        case 'running':
            return 'blue';
        case 'resetting':
            return 'lightblue';
        case 'testing':
            return 'darkgoldenrod';
        default:
            return 'black';
    }
};

export const downloadCSVFromJson = (
    filename: string,
    data: Record<string, any>[],
) => {
    // convert JSON to CSV
    const replacer = (key: string, value: any) => (value === null ? '' : value); // specify how you want to handle null values here
    const header = Object.keys(data[0]);
    const csv = data.map((row) =>
        header
            .map((fieldName) => JSON.stringify(row[fieldName], replacer))
            .join(','),
    );
    csv.unshift(header.join(','));

    // Create link and download
    const link = document.createElement('a');
    link.setAttribute(
        'href',
        'data:text/csv;charset=utf-8,%EF%BB%BF' +
            encodeURIComponent(csv.join('\r\n')),
    );
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};
